import Logger from './logger';
import Emitter from './emitter';
import Engine from './engine';
import Socket from './socket';
import on from './on';
import { DEFAULT_OPTS } from './utils';

const debug = (...args) => Logger.info(...args);

export default class Controller extends Emitter {
    constructor(uri, opts = {}) {
        super();

        opts = { ...DEFAULT_OPTS, ...opts };
        Logger.debug = opts.debug;

        this.opts = opts;
        this.uri = uri;
        this.format = opts.format.toLowerCase();
        this.timeout = opts.timeout;
        this.autoConnect = opts.autoConnect;
        this.reconnection = opts.reconnection;
        this.reconnectionAttempts = opts.reconnectionAttempts;
        this.reconnectionDelay = opts.reconnectionDelay;
        this.reconnectTimeoutId = 0;
        this.reconnectionCount = 0;
        this.subs = [];
        this.nsps = {};
        this.engine = undefined;

        this._reconnecting = false;
        this._readyState = 'closed';

        this.autoConnect && this.open();
    }
    open(fn) {
        debug(`readyState ${this._readyState}`);
        if (~this._readyState.indexOf('open')) return this;

        debug(`opening ${this.uri}`);
        this.engine = new Engine(this.uri);
        const socket = this.engine;
        const self = this;
        this._readyState = 'opening';
        this.skipReconnect = false;

        // emit 'open'
        const openSubDestroy = on(socket, 'open', function() {
            self.onopen();
            fn && fn();
        });

        // emit `error`
        const errorSub = on(socket, 'error', err => {
            debug('error');
            self.cleanup();
            self._readyState = 'closed';
            this.emitReserved('error', err);
            if (fn) {
                fn(err);
            } else {
                // reconnectOnOpen?
            }
        });

        if (this.timeout) {
            const timeout = this.timeout;
            debug(`connect attempt will timeout after ${timeout}`);

            if (timeout === 0) {
                openSubDestroy();
            }

            const timer = setTimeout(() => {
                debug(`connect attempt timed out after ${timeout}`);
                openSubDestroy();
                socket.close();
                socket.emit('error', new Error('timeout'));
            }, timeout);

            if (this.opts.autoUnref) {
                // @TODO: setTimeoutFn...
                //timer.unref();
            }

            this.subs.push(function subDestroy() {
                clearTimeout(timer);
            });
        }

        this.subs.push(openSubDestroy);
        this.subs.push(errorSub);

        return this;
    }
    connect(fn) {
        return this.open(fn);
    }
    onopen() {
        debug('open');

        this.cleanup();
        this._readyState = 'open';
        this.emitReserved('open');

        const socket = this.engine;
        this.subs.push(
            on(socket, 'message', this.onmessage.bind(this)),
            on(socket, 'data', this.ondata.bind(this)),
            on(socket, 'error', this.onerror.bind(this)),
            on(socket, 'close', this.onclose.bind(this))
        );
    }
    onmessage(event) {
        this.emitReserved('message', event);
    }
    ondata(event) {
        this.emitReserved('data', event);
    }
    onerror(err) {
        debug('error', err);
        this.emitReserved('error', err);
    }
    onclose(reason = '') {
        debug(`closed due to ${reason}`);

        this.cleanup();
        this._readyState = 'closed';
        this.emitReserved('close', reason);

        if (this.reconnection && !this.skipReconnect) {
            this.reconnect();
        }
    }
    cleanup() {
        debug('cleanup');

        this.subs.forEach(subDestroy => subDestroy());
        this.subs.length = 0;
    }
    socket(nsp, opts = {}) {
        let socket = this.nsps[nsp];
        if (!socket) {
            socket = new Socket(this, nsp, { ...DEFAULT_OPTS, ...opts });
            this.nsps[nsp] = socket;
        }

        return socket;
    }
    destroy() {
        const nsps = Object.keys(this.nsps);

        for (const nsp of nsps) {
            const socket = this.nsps[nsp];

            if (socket.active) {
                debug(`socket "${nsp}" is still active, skipping close`);
                return;
            }
        }

        this._close();
    }
    _close() {
        debug('disconnect');
        this.skipReconnect = true;
        this._reconnecting = false;
        this.onclose('forced close');
        if (this.engine) this.engine.close();
    }
    disconnect() {
        return this._close();
    }
    reconnect() {
        if (this._reconnecting || this.skipReconnect) return this;

        const self = this;

        if (this.reconnectionCount >= this.reconnectionAttempts) {
            debug('reconnect failed');
            this.emitReserved('reconnect_failed');
            this._reconnecting = false;
        } else {
            const { reconnectionDelay: delay } = this;
            debug(`will wait ${delay}ms before reconnect attempt`);

            this._reconnecting = true;
            // const timer = setTimeout(() => {
            setTimeout(() => {
                if (self.skipReconnect) return;

                debug('attempting reconnect');
                this.emitReserved('reconnect_attempt', this.reconnectionCount);
                if (self.skipReconnect) return;

                self.open(err => {
                    if (err) {
                        debug('reconnect attempt error');
                        self._reconnecting = false;
                        self.reconnect();
                        this.emitReserved('reconnect_error', err);
                    } else {
                        debug('reconnect success');
                        self.onreconnect();
                    }
                });
            }, delay);

            if (this.opts.autoUnref) {
                //timer.unref();
            }
        }
    }
    onreconnect() {
        this._reconnecting = false;
        this.reconnectionCount++;
        this.emitReserved('reconnect', this.reconnectionCount);
    }
}
